import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Segment, Image, Loader, List, Dropdown } from 'semantic-ui-react';
import { history, store } from '../../helpers';
import Strings from '../../common/Strings';
import { inviteActions } from '../../actions';
import { PageNotFound } from '../PageNotFound';
import Constant from '../../common/Constant';
import { projectDetailsActions } from '../../actions/projectDetails';
import {
  dropSecondsAndMillisFromWorkLogMillis,
  getHoursFromMillis,
  getLoggedTimeFromHours,
} from '../../helpers/workLog';
import { HourScatterChart } from '../common/HourScatterChart';
import '../Common.css';
import './ProjectDetails.css';
import '../common/HourScatterChart.css';
import { exportProjectCsv } from '../../helpers/export';
import config from '../../config';

const tinycolor = require('tinycolor2');

const moment = require('moment');

class ProjectDetails extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      project: null,
      graphDate: Strings.COMMON.lifetime,
      selectedTeamMembers: null,
    };

    if (!props.isProjectsLoading) {
      this.updateProjectFromStore(true);
    }
  }

  componentWillReceiveProps(nextProps) {
    if (!nextProps.isProjectsLoading) {
      this.updateProjectFromStore();
    }
  }

  componentWillUnmount() {
    projectDetailsActions.stopSync();
  }

  updateProjectFromStore(isConstructor) {
    const projectId = history.location.pathname.replace('/projects/', '');
    const project = store.getState().projects.find(p => p.id === projectId);

    // Initial graph data
    if (!this.state.project && project) {
      this.props.dispatch(
        projectDetailsActions.fetchWorkLogs(project, this.state.graphDate),
      );
    }

    // Update selected members only from initial props
    let selectedTeamMembers;
    if (this.state.selectedTeamMembers) {
      selectedTeamMembers = this.state.selectedTeamMembers;
    } else {
      selectedTeamMembers = [];
      if (project) {
        project.teamMembers.forEach(t => selectedTeamMembers.push(t.email));
      }
    }

    if (isConstructor) {
      this.state = {
        project,
        graphDate: Strings.COMMON.lifetime,
        selectedTeamMembers,
      };
    } else {
      this.setState({
        project,
        selectedTeamMembers,
      });
    }
  }

  onClickProjects = () => {
    history.replace('/projects');
  };

  onClickInvite = () => {
    const { project } = this.state;
    history.push('#invite', { project });
  };

  onClickEdit = () => {
    const { project } = this.state;
    history.push('#editProject', { project });
  };

  onClickExport = workLogs => {
    if (workLogs.length === 0) return;
    exportProjectCsv(this.state.project, workLogs);
  };

  onClickClose = () => {
    const { project } = this.state;
    history.push('#closeProject', { project });
  };

  onClickReopen = () => {
    if (
      this.props.subscription.status !== 'active' &&
      this.countActiveProjects() >= config.MAX_ACTIVE_PROJECTS_FREE
    ) {
      history.push('#premium');
    } else {
      const { project } = this.state;
      history.push('#reopenProject', { project });
    }
  };

  countActiveProjects() {
    let count = 0;
    this.props.projects.forEach(p => {
      if (p.status === 'active') count += 1;
    });
    return count;
  }

  onClickRemind = teamMember => {
    const { companyName, defaultCurrency } = store.getState().user;
    const { project } = this.state;
    this.props.dispatch(
      inviteActions.inviteTeamReminder(
        companyName,
        project,
        teamMember.email,
        defaultCurrency,
      ),
    );
  };

  onClickEditTeamMember = teamMember => {
    const { project } = this.state;
    history.push('#editProjectMember', { project, teamMember });
  };

  onClickDeleteTeamMember = teamMember => {
    const { project } = this.state;
    history.push('#removeProjectMember', { project, teamMember });
  };

  onClickDeletePendingInvitation = teamMember => {
    const { project } = this.state;
    history.push('#removeInvitation', { project, teamMember });
  };

  renderProjectDetails(filteredWorkLogs) {
    if (!this.state.project) {
      return null;
    }

    const { color, closedAt, createdAt } = this.state.project;
    const colorWithAlpha = tinycolor(color)
      .setAlpha(0.2)
      .toRgbString();

    const { workLogs } = this.props;
    const { project, graphDate } = this.state;
    let { selectedTeamMembers } = this.state;
    const contributedTeamMembers = [];
    const contributedTeamMembersMap = new Map();
    const teamMemberHourlyRateMap = new Map();
    const labels = [];
    const chartData = [];
    let chartYMax = 8.0;
    let chartXMax = 1;
    let totalLoggedHours = 0.0;
    let totalCost = 0.0;

    // Populate team based on all work logs
    workLogs.forEach(w => {
      if (!contributedTeamMembersMap.has(w.userEmail)) {
        contributedTeamMembersMap.set(w.userEmail, {
          workLog: w,
          totalLoggedMillis: w.totalLoggedMillis,
        });
      } else {
        const newTotalLoggedMillis =
          contributedTeamMembersMap.get(w.userEmail).totalLoggedMillis +
          w.totalLoggedMillis;
        contributedTeamMembersMap.set(w.userEmail, {
          workLog: w,
          totalLoggedMillis: newTotalLoggedMillis,
        });
      }
    });

    // Only populate positive total logged hours
    contributedTeamMembersMap.forEach(value => {
      if (value.totalLoggedMillis > 0) {
        const teamMember = store
          .getState()
          .teamMembers.find(
            t =>
              t.email === value.workLog.userEmail ||
              t.uid === value.workLog.userUid,
          );
        if (teamMember) {
          contributedTeamMembers.push(teamMember);

          // Store cost of project team member to calculate total cost
          const projectTeamMember = project.teamMembers.find(
            t => t.uid === teamMember.uid || t.email === teamMember.email,
          );
          if (projectTeamMember) {
            teamMemberHourlyRateMap.set(
              projectTeamMember.uid,
              projectTeamMember.hourlyRate,
            );
          }
        }
      }
    });

    // Populate work logs into dates map
    const workLogMap = new Map();
    filteredWorkLogs.forEach(w => {
      const millisToUse = dropSecondsAndMillisFromWorkLogMillis(
        w.totalLoggedMillis,
      );
      const hoursToUse = getHoursFromMillis(millisToUse);
      if (workLogMap.has(w.date)) {
        const newTotalLoggedMillis = workLogMap.get(w.date) + millisToUse;
        workLogMap.set(w.date, newTotalLoggedMillis);
      } else {
        workLogMap.set(w.date, millisToUse);
      }

      totalLoggedHours += hoursToUse;

      // Increase total cost
      const hourlyRate = teamMemberHourlyRateMap.get(w.userUid);
      if (hourlyRate) {
        totalCost += hoursToUse * hourlyRate;
      }
    });

    let startDate;
    let endDate;
    if (graphDate === Strings.COMMON.lifetime) {
      startDate = moment(createdAt).startOf('month');
      endDate = moment().endOf('month');
      // Check if there is earlier date to use than when project was created
      if (filteredWorkLogs.length > 0) {
        const altStartDate = moment(filteredWorkLogs[0].date);
        if (altStartDate.isBefore(startDate)) {
          startDate = altStartDate.startOf('month');
        }
      }
    } else {
      startDate = moment(graphDate).startOf('month');
      endDate = moment(graphDate).endOf('month');
    }

    while (startDate.isSameOrBefore(endDate)) {
      labels.push(startDate.format('MMM D, YYYY'));
      const loggedMillis = workLogMap.get(
        startDate.format(Constant.DATE_FORMAT),
      );
      if (loggedMillis) {
        const hours = getHoursFromMillis(loggedMillis);
        chartData.push({ x: chartXMax, y: hours });
        if (hours > chartYMax) {
          chartYMax = hours;
        }
      } else {
        chartData.push({ x: chartXMax, y: 0 });
      }

      chartXMax++;
      startDate = startDate.add(1, 'days');
    }

    const graphData = {
      datasets: [
        {
          fill: true,
          borderColor: colorWithAlpha,
          borderWidth: 1,
          showLine: true,
          backgroundColor: colorWithAlpha,
          pointBorderColor: color,
          pointBackgroundColor: color,
          pointHoverRadius: 4,
          pointHoverBackgroundColor: color,
          pointHoverBorderColor: color,
          pointRadius: 2,
          pointHitRadius: 8,
          data: chartData,
        },
      ],
    };

    const projectTeamMembersMap = new Map();
    project.teamMembers.forEach(t => projectTeamMembersMap.set(t.email, t));
    contributedTeamMembers.sort((a, b) => {
      if (a.name && b.name) {
        return a.name.localeCompare(b.name);
      }
      return a.email.localeCompare(b.email);
    });

    const ActiveTeamMembers = contributedTeamMembers.map(teamMember => {
      const selected = selectedTeamMembers.find(
        email => teamMember.email === email,
      );
      let container;
      let text;
      if (selected) {
        container = 'project-details-team-member-container-selected';
        text = 'project-details-team-member-selected';
      } else {
        container = 'project-details-team-member-container';
        text = 'project-details-team-member';
      }
      let displayName = teamMember.name;
      if (!displayName) {
        const projectMember = projectTeamMembersMap.get(teamMember.email);
        displayName = projectMember ? projectMember.name : teamMember.email;
      }
      return (
        <div
          className={container}
          onClick={() => {
            if (selected) {
              selectedTeamMembers = selectedTeamMembers.filter(
                email => email !== teamMember.email,
              );
            } else {
              selectedTeamMembers.push(teamMember.email);
            }
            this.setState({ selectedTeamMembers });
          }}
        >
          <p className={text}>{displayName}</p>
        </div>
      );
    });

    let ClosedAt;
    if (closedAt) {
      const closedAtText = Strings.PROJECT_DETAILS.formatString(
        Strings.PROJECT_DETAILS.closedOn,
        moment(closedAt).format('YYYY/MM/DD'),
      );
      ClosedAt = <p className="closed-on-text">{closedAtText}</p>;
    }

    const contributedTeamMembersCount = contributedTeamMembers.filter(t =>
      selectedTeamMembers.find(email => email === t.email),
    ).length;

    const stats = [
      {
        label: Strings.PROJECT_DETAILS.activeEmployees,
        value: contributedTeamMembersCount,
      },
      {
        label: Strings.COMMON.totalHours,
        value: getLoggedTimeFromHours(totalLoggedHours),
      },
      {
        label: Strings.COMMON.totalCost,
        totalCost,
        currency: store.getState().user.defaultCurrency,
      },
    ];

    return (
      <div className="project-details-container">
        {ClosedAt}
        <HourScatterChart
          data={graphData}
          labels={labels}
          chartXMax={chartXMax}
          chartYMax={chartYMax}
          stats={stats}
          statsColor={color}
        />
        <p className="project-details-team-text">
          {Strings.PROJECT_DETAILS.teamSection}
        </p>
        <div className="project-details-team-container">
          {ActiveTeamMembers}
        </div>
      </div>
    );
  }

  renderSidePanelContent(activeTeamMembers, pendingTeamMembers) {
    const { status } = this.state.project;

    if (status === 'closed') {
      return (
        <div className="project-details-team-empty-container">
          <p className="project-details-team-empty-title">
            {Strings.PROJECT_DETAILS.projectClosedTitle}
          </p>
          <p className="project-details-team-empty-body">
            {Strings.PROJECT_DETAILS.projectClosedBody}
          </p>
          <button
            id="invite-button"
            className="common-light-button"
            onClick={this.onClickReopen}
          >
            {Strings.PROJECT_DETAILS.reopenProject}
          </button>
        </div>
      );
    }

    let EmptySection;
    let ActiveTeamMembersSection;
    let PendingTeamMembersSection;
    let ReopenProjectSection;

    if (activeTeamMembers.length === 0 && pendingTeamMembers.length === 0) {
      EmptySection = (
        <div className="project-details-team-empty-container">
          <Image
            className="project-details-team-empty-image"
            src="/empty/activity_empty.png"
          />
          <p className="project-details-team-empty-title">
            {Strings.PROJECT_DETAILS.teamEmptyTitle}
          </p>
          <p className="project-details-team-empty-body">
            {Strings.PROJECT_DETAILS.teamEmptyBody}
          </p>
          <button
            id="invite-button"
            className="common-green-button"
            onClick={this.onClickInvite}
          >
            {Strings.PROJECT_DETAILS.invite}
          </button>
        </div>
      );
    } else {
      if (activeTeamMembers.length > 0) {
        const ActiveTeamMembers = activeTeamMembers.map(teamMember => {
          const removingMember = this.props.removingProjectMembers.find(
            email => email === teamMember.email,
          );
          const companyMember = this.props.teamMembers.find(
            m => m.uid === teamMember.uid,
          );
          const name = teamMember.name || (companyMember && companyMember.name);
          return (
            <List.Item
              className="project-details-pending-list-item"
              key={teamMember.email}
            >
              <div className="project-details-pending-list-item-content">
                <div style={{ flexGrow: 1 }}>
                  <p className="project-details-active-list-item-name">
                    {name || teamMember.email}
                  </p>
                  {name && (
                    <p className="project-details-pending-list-item-email">
                      {teamMember.email}
                    </p>
                  )}
                </div>
                <p className="project-details-active-list-item-rate">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: store.getState().user.defaultCurrency,
                    maximumSignificantDigits: 2,
                  }).format(teamMember.hourlyRate)}
                  {Strings.PROJECT_DETAILS.perHour}
                </p>
                {removingMember && (
                  <Loader
                    active
                    inline
                    size="mini"
                    style={{ marginRight: 10 }}
                  />
                )}
                <Image
                  className="project-details-list-item-edit"
                  src="/icon/edit.svg"
                  disabled={removingMember}
                  onClick={() => {
                    if (!removingMember) {
                      this.onClickEditTeamMember(teamMember);
                    }
                  }}
                />
                <Image
                  className="project-details-list-item-delete"
                  src="/icon/delete.svg"
                  disabled={removingMember}
                  onClick={() => {
                    if (!removingMember) {
                      this.onClickDeleteTeamMember(teamMember);
                    }
                  }}
                />
              </div>
              <div className="project-details-side-panel-divider" />
            </List.Item>
          );
        });

        ActiveTeamMembersSection = (
          <div style={{ marginBottom: '2rem' }}>
            <List style={{ marginTop: 0 }}>{ActiveTeamMembers}</List>
          </div>
        );
      }

      if (pendingTeamMembers.length > 0) {
        const PendingTeamMembers = pendingTeamMembers.map(teamMember => {
          const updatingInvitation = this.props.updatingInvitations.find(
            email => email === teamMember.email,
          );
          return (
            <List.Item
              className="project-details-pending-list-item"
              key={teamMember.email}
            >
              <div className="project-details-pending-list-item-content">
                <div style={{ flexGrow: 1 }}>
                  <p className="project-details-pending-list-item-name">
                    {teamMember.name || teamMember.email}
                  </p>
                  {teamMember.name && (
                    <p className="project-details-pending-list-item-email">
                      {teamMember.email}
                    </p>
                  )}
                </div>
                {!updatingInvitation && (
                  <p
                    className="project-details-pending-list-item-remind"
                    onClick={() => this.onClickRemind(teamMember)}
                  >
                    {Strings.PROJECT_DETAILS.remind}
                  </p>
                )}
                {updatingInvitation && (
                  <Loader
                    active
                    inline
                    size="mini"
                    style={{ marginRight: 10 }}
                  />
                )}
                <Image
                  className="project-details-list-item-delete"
                  disabled={updatingInvitation}
                  src="/icon/delete.svg"
                  onClick={() => {
                    if (!updatingInvitation) {
                      this.onClickDeletePendingInvitation(teamMember);
                    }
                  }}
                />
              </div>
              <div className="project-details-side-panel-divider" />
            </List.Item>
          );
        });

        PendingTeamMembersSection = (
          <div>
            <p className="project-details-pending-header">
              {Strings.PROJECT_DETAILS.pending}
            </p>
            <p className="project-details-pending-body">
              {Strings.PROJECT_DETAILS.pendingInfo}
            </p>
            <div className="project-details-side-panel-divider" />
            <List style={{ marginTop: 0 }}>{PendingTeamMembers}</List>
          </div>
        );
      }
    }

    if (status === 'active') {
      ReopenProjectSection = (
        <div className="project-details-team-empty-container">
          <button
            id="close-button"
            className="common-light-button"
            onClick={this.onClickClose}
          >
            {Strings.PROJECT_DETAILS.closeProject}
          </button>
          <p className="project-details-team-empty-body">
            {Strings.PROJECT_DETAILS.closeProjectInfo}
          </p>
        </div>
      );
    }

    return (
      <div>
        {EmptySection}
        {ActiveTeamMembersSection}
        {PendingTeamMembersSection}
        {ReopenProjectSection}
      </div>
    );
  }

  getDropDownData(lifetime, project, graphDate, workLogs) {
    let startDate = moment(project.createdAt).startOf('month');
    // Check if there is earlier date to use than when project was created
    if (workLogs.length > 0) {
      const altStartDate = moment(workLogs[0].date);
      if (altStartDate.isBefore(startDate)) {
        startDate = altStartDate.startOf('month');
      }
    }

    let now = moment().startOf('month');
    const months = [lifetime, now.format(Constant.DATE_FORMAT)];
    let monthsDiff = Math.ceil(now.diff(startDate, 'months', true));
    while (monthsDiff > 0) {
      now = now.add(-1, 'months');
      monthsDiff--;
      months.push(now.format(Constant.DATE_FORMAT));
    }
    const dropDownText =
      graphDate === lifetime ? lifetime : moment(graphDate).format('MMMM YYYY');

    return {
      months,
      dropDownText,
    };
  }

  render() {
    const { isProjectsLoading, dispatch, workLogs } = this.props;
    if (isProjectsLoading) {
      return null;
    }

    const { project, graphDate, selectedTeamMembers } = this.state;
    if (!project) {
      return <PageNotFound />;
    }

    const { teamMembers } = project;
    const activeTeamMembers = teamMembers.filter(t => t.status === 'active');
    const pendingTeamMembers = teamMembers.filter(t => t.status === 'invited');
    const isActive = project.status === 'active';

    // Populate drop down months based on project creation date
    const lifetime = Strings.COMMON.lifetime;
    const { months, dropDownText } = this.getDropDownData(
      lifetime,
      project,
      graphDate,
      workLogs,
    );

    const filteredWorkLogs = workLogs.filter(w =>
      selectedTeamMembers.find(email => email === w.userEmail),
    );

    return (
      <Segment basic className="project-details-root">
        <Segment basic className="project-details">
          <div className="common-top-panel">
            <div className="project-details-title-container">
              <div className="project-details-title-top-container">
                <p id="project-details-title" onClick={this.onClickProjects}>
                  {Strings.PROJECTS.projects}
                </p>
                <p className="project-details-title-slash">/</p>
              </div>
              <div className="project-details-title-bottom-container">
                <div
                  className="project-details-color"
                  style={{ backgroundColor: project.color }}
                />
                <h1
                  id="project-details-name"
                  className="common-top-panel-title"
                >
                  {project.name}
                </h1>
                {isActive && (
                  <Image
                    id="project-details-edit"
                    src="/icon/edit.svg"
                    onClick={this.onClickEdit}
                  />
                )}
              </div>
            </div>
            <div style={{ flexGrow: 1 }} />
            <Dropdown
              text={dropDownText}
              basic
              className="graph-date-drop-down"
              fluid
              icon={<div className="graph-date-drop-down-icon" />}
            >
              <Dropdown.Menu>
                {months.map(month => (
                  <Dropdown.Item
                    value={month}
                    onClick={(e, { value }) => {
                      if (this.state.graphDate !== value) {
                        dispatch(
                          projectDetailsActions.fetchWorkLogs(project, value),
                        );
                        this.setState({ graphDate: value });
                      }
                    }}
                    className="graph-date-drop-down-item"
                  >
                    {month === lifetime
                      ? lifetime
                      : moment(month).format('MMMM YYYY')}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
            <Image
              id="project-details-export"
              className={
                filteredWorkLogs.length > 0
                  ? 'project-details-export-enabled'
                  : 'project-details-export-disabled'
              }
              src="/icon/export.png"
              disabled={filteredWorkLogs.length === 0}
              onClick={() => this.onClickExport(filteredWorkLogs)}
            />
          </div>
          <div className="project-details-content">
            {this.renderProjectDetails(filteredWorkLogs)}
            <Image className="app-footer-logo" src="/circles-grey.png" />
          </div>
        </Segment>
        <div className="common-side-panel">
          <div className="common-top-panel">
            <h1 id="team-title" className="common-side-panel-title">
              {Strings.PROJECT_DETAILS.team}
            </h1>
            {isActive && (
              <p id="invite-text" onClick={this.onClickInvite}>
                {Strings.PROJECT_DETAILS.inviteWithPlus}
              </p>
            )}
          </div>
          <div className="project-details-side-panel-content">
            {this.renderSidePanelContent(activeTeamMembers, pendingTeamMembers)}
          </div>
        </div>
      </Segment>
    );
  }
}

ProjectDetails.propTypes = {
  isProjectsLoading: PropTypes.bool.isRequired,
  updatingInvitations: PropTypes.arrayOf(PropTypes.string.isRequired)
    .isRequired,
  removingProjectMembers: PropTypes.arrayOf(PropTypes.string.isRequired)
    .isRequired,
  projects: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      currencyCode: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
    }),
  ).isRequired,
  workLogs: PropTypes.arrayOf(
    PropTypes.shape({
      userUid: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
      totalLoggedMillis: PropTypes.number.isRequired,
    }),
  ).isRequired,
  subscription: PropTypes.shape({
    status: PropTypes.string.isRequired,
  }).isRequired,
  teamMembers: PropTypes.arrayOf(
    PropTypes.shape({
      uid: PropTypes.string.isRequired,
      name: PropTypes.string,
    }),
  ).isRequired,
};

const mapStateToProps = state => ({
  isProjectsLoading: state.uiState.isProjectsLoading,
  updatingInvitations: state.uiState.updatingInvitations,
  removingProjectMembers: state.uiState.removingProjectMembers,
  projects: state.projects,
  workLogs: state.projectDetails,
  subscription: state.premiumSubscription.subscription,
  teamMembers: state.teamMembers,
});

export default connect(mapStateToProps)(ProjectDetails);



// WEBPACK FOOTER //
// ./src/components/projects/ProjectDetails.js